import React, { useCallback, useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRealtime } from '../lib/useRealtime';
import { Clock, ChefHat, Zap } from 'lucide-react';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from '../components/PlanUpgradeNotice';
import { hasExtra, isKitchenItem } from '../lib/orderFlow';

function Timer({ since }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
    const sec = Math.floor((now - new Date(since).getTime()) / 1000);
    const m = Math.floor(sec / 60), s = sec % 60;
    const urgent = sec > 15 * 60;
    return <span style={{ color: urgent ? 'var(--neon-magenta)' : 'var(--text-muted)', fontWeight: 600 }}><Clock size={13} style={{ verticalAlign: '-2px' }} /> {m}:{String(s).padStart(2, '0')}</span>;
}

export default function KitchenPage() {
    const { user, restaurant } = useAuth();
    const [orders, setOrders] = useState([]);

    const reload = useCallback(() => {
        api.get('/r/orders').then(r => {
            // Show first-wave orders to cook (CONFIRMED/IN_KITCHEN/READY) AND any order
            // with an extra batch in the kitchen: sent (to start), cooking (in prep),
            // or ready (cooked, shown in "Pregătite" until the waiter picks it up).
            // Terminal orders are NEVER shown — a SERVED/PAID/CANCELLED order can't be
            // advanced, so a lingering extra flag on one would stick forever (ghost ticket).
            setOrders(r.data.items.filter(o =>
                !['SERVED', 'PAID', 'CANCELLED'].includes(o.status)
                && (['CONFIRMED', 'IN_KITCHEN', 'READY'].includes(o.status)
                    || hasExtra(o, 'sent') || hasExtra(o, 'cooking') || hasExtra(o, 'ready'))
            ));
        });
    }, []);

    useEffect(() => { reload(); }, [reload]);
    useRealtime(user?.restaurant_id, (msg) => {
        if (['new_order', 'order_updated'].includes(msg.type)) reload();
    });

    // One card per table: advance the whole card (main wave + any extra) together.
    const advanceCard = async (id, action) => {
        try { await api.post(`/r/orders/${id}/kitchen`, { action }); reload(); }
        catch (e) { alert(formatApiError(e)); }
    };

    // Stage of the single card = the least-advanced thing the kitchen still owes.
    const kitchenStage = (o) => {
        if (o.status === 'CONFIRMED' || hasExtra(o, 'sent')) return 'to_prepare';
        if (o.status === 'IN_KITCHEN' || hasExtra(o, 'cooking')) return 'cooking';
        if (o.status === 'READY' || hasExtra(o, 'ready')) return 'ready';
        return null;
    };

    // Items shown on the card, annotated. Hidden from the kitchen:
    //   - non-kitchen items (drinks/desserts that skip the kitchen entirely)
    //   - client extras still 'new' (waiter hasn't confirmed yet)
    // "Served" is derived per-item now (serve_state for regular lines, extra_state for
    // extras) — relying on the order status would miss partially-served orders.
    const kitchenLines = (o) => {
        const orderClosed = ['SERVED', 'PAID'].includes(o.status);
        return (o.items || []).map((it, i) => {
            const isExtra = it.is_extra && it.source === 'client';
            const served = isExtra
                ? it.extra_state === 'served'
                : (it.serve_state === 'served' || orderClosed);
            const isNew = isExtra && ['sent', 'cooking'].includes(it.extra_state);
            const unconfirmed = isExtra && (it.extra_state || 'new') === 'new';
            const skipsKitchen = !isKitchenItem(it);
            return { ...it, _i: i, _served: served, _new: isNew, _hidden: unconfirmed || skipsKitchen };
        }).filter(it => !it._hidden);
    };

    const cols = [
        { stage: 'to_prepare', label: 'De preparat', action: 'start', btn: 'Începe gătirea' },
        { stage: 'cooking', label: 'În pregătire', action: 'ready', btn: 'GATA' },
        { stage: 'ready', label: 'Pregătite — preia ospătarul', action: null, btn: null },
    ];

    if (!hasPlanFeature(restaurant, 'kitchen_kds')) {
        return (
            <div className="fade-in-up" data-testid="kitchen-page">
                <div className="kicker">Kitchen Display System</div>
                <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}><ChefHat style={{ verticalAlign: '-6px' }} size={32} /> Bucătărie live.</h1>
                <PlanUpgradeNotice featureLabel="Kitchen Display System (KDS)" />
            </div>
        );
    }

    return (
        <div className="fade-in-up" data-testid="kitchen-page">
            <div className="kicker">Kitchen Display System</div>
            <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}><ChefHat style={{ verticalAlign: '-6px' }} size={32} /> Bucătărie live.</h1>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                {cols.map(c => {
                    // Skip cards with no visible kitchen lines — drinks-only orders never appear here.
                    const list = orders.filter(o => kitchenStage(o) === c.stage && kitchenLines(o).length > 0);
                    return (
                        <div key={c.stage} className="kds-column" data-testid={`kds-col-${c.stage}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                                <h3 className="headline" style={{ margin: 0, fontSize: '1.2rem' }}>{c.label}</h3>
                                <span className="badge badge-muted">{list.length}</span>
                            </div>
                            <div>
                                {list.map(o => {
                                    const urgent = (Date.now() - new Date(o.created_at).getTime()) > 15 * 60 * 1000;
                                    const isNew = hasExtra(o, 'sent') || hasExtra(o, 'cooking');
                                    return (
                                        <div key={o.id} className={`kds-ticket ${urgent ? 'urgent' : ''}`}
                                             style={isNew ? { borderColor: 'var(--neon-magenta)' } : undefined}
                                             data-testid={`kds-ticket-${o.id}`}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Masa {o.table_number}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {isNew && <span className="badge badge-magenta"><Zap size={11} style={{ marginRight: 2, verticalAlign: '-1px' }} /> EXTRA NOU</span>}
                                                    <Timer since={o.created_at} />
                                                </div>
                                            </div>
                                            <ul className="kds-items">
                                                {kitchenLines(o).map(it => (
                                                    <li key={it._i} className={`kds-line${it._served ? ' kds-line--served' : ''}${it._new ? ' kds-line--new' : ''}`}>
                                                        {it._new ? '⚡ ' : '• '}{it.quantity}× {it.product_name}{it.variant_name ? ` · ${it.variant_name}` : ''}
                                                        {it._served ? ' (SERVITĂ)' : ''}
                                                        {it.notes ? <em style={{ color: 'var(--amber)' }}> ({it.notes})</em> : null}
                                                    </li>
                                                ))}
                                            </ul>
                                            {c.action && (
                                                <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => advanceCard(o.id, c.action)} data-testid={`kds-advance-${o.id}`}>{c.btn}</button>
                                            )}
                                        </div>
                                    );
                                })}
                                {!list.length && <div style={{ color: 'var(--text-faint)', textAlign: 'center', padding: '1.5rem 0', fontSize: '0.88rem' }}>Coloană liberă</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
